import { useState, useEffect, useRef, useCallback } from 'react'
import { useFStore } from '@/stores/useFStore'
import { FFoolscapContent } from './FFoolscapContent'
import { FEditsPanel } from './FEditsPanel'
import { FCardsPanel } from './FCardsPanel'
import { FAttachmentsPanel } from './FAttachmentsPanel'
import { FileText, Library, LibraryBig, GitCompare, Paperclip } from 'lucide-react'
import { cn } from '@/lib/utils'

type TabType = 'document' | 'edits' | 'cards' | 'attachments'

export function FDocumentPanel() {
  const {
    foolscapMarkdown,
    foolscapMarkdownAnnotated,
    integratedCardIds,
    surfacedCards,
    workflowStatus,
    sessionId,
    setSelectedCardId,
    cardsEnabled,
    toggleCardsEnabled,
  } = useFStore()
  const isStreaming = workflowStatus === 'streaming'
  const [activeTab, setActiveTab] = useState<TabType>('document')

  // Track previous state for auto-switching to Edits tab
  const prevWorkflowStatusRef = useRef(workflowStatus)
  const prevAnnotatedDocRef = useRef(foolscapMarkdownAnnotated)
  const userSwitchedTabRef = useRef(false)

  // Handle manual tab clicks
  const handleTabClick = (tab: TabType) => {
    userSwitchedTabRef.current = true
    setActiveTab(tab)
  }

  // Reset to document tab when session changes
  useEffect(() => {
    setActiveTab('document')
    userSwitchedTabRef.current = false
    prevAnnotatedDocRef.current = null
  }, [sessionId])

  // Auto-switch to Edits tab when workflow completes with changes (Zinsser pattern)
  useEffect(() => {
    const wasActive = prevWorkflowStatusRef.current === 'streaming' || prevWorkflowStatusRef.current === 'processing'
    const justCompleted = workflowStatus === 'idle' && wasActive
    const annotatedDocJustArrived = foolscapMarkdownAnnotated && !prevAnnotatedDocRef.current

    console.log('[FDocumentPanel] useEffect:', {
      prevStatus: prevWorkflowStatusRef.current,
      currentStatus: workflowStatus,
      wasActive,
      justCompleted,
      annotatedDocLength: foolscapMarkdownAnnotated?.length || 0,
      prevAnnotatedLength: prevAnnotatedDocRef.current?.length || 0,
      annotatedDocJustArrived,
      userSwitchedTab: userSwitchedTabRef.current,
    })

    prevWorkflowStatusRef.current = workflowStatus

    // Switch to Edits tab when:
    // 1. Workflow just completed AND annotated doc exists, OR
    // 2. Annotated doc just arrived AND workflow is idle
    if ((justCompleted && foolscapMarkdownAnnotated) || (annotatedDocJustArrived && workflowStatus === 'idle')) {
      console.log('[FDocumentPanel] Switching to Edits tab')
      if (!userSwitchedTabRef.current) {
        setActiveTab('edits')
      }
      userSwitchedTabRef.current = false
    }

    prevAnnotatedDocRef.current = foolscapMarkdownAnnotated
  }, [workflowStatus, foolscapMarkdownAnnotated])

  // Handle card click from document - switch to cards tab and select the card
  const handleCardClick = useCallback((cardId: string) => {
    setSelectedCardId(cardId)
    setActiveTab('cards')
    userSwitchedTabRef.current = true
  }, [setSelectedCardId])

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Tab bar */}
      <div className="flex-shrink-0 overflow-x-auto scrollbar-none px-2 md:px-3 py-2 border-b border-border">
        <div className="flex items-center gap-0.5 md:gap-1">
          {/* Document tab */}
          <button
            onClick={() => handleTabClick('document')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
              activeTab === 'document'
                ? 'bg-foreground/10 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            )}
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Document</span>
          </button>

          {/* Edits tab (Zinsser pattern) */}
          <button
            onClick={() => handleTabClick('edits')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
              activeTab === 'edits'
                ? 'bg-foreground/10 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            )}
          >
            <GitCompare className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Edits</span>
            {foolscapMarkdownAnnotated && (
              <span className="ml-0.5 px-1.5 py-0.5 bg-amber-500 text-white font-medium rounded-full text-xs">
                New
              </span>
            )}
          </button>

          {/* Cards tab */}
          <button
            onClick={() => handleTabClick('cards')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
              activeTab === 'cards'
                ? 'bg-foreground/10 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            )}
          >
            <Library className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Cards</span>
            {surfacedCards.length > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 bg-muted rounded-full text-xs">
                {surfacedCards.length}
              </span>
            )}
            {integratedCardIds.length > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 bg-green-500/20 text-green-600 dark:text-green-400 rounded-full text-xs">
                {integratedCardIds.length}
              </span>
            )}
          </button>

          {/* Attachments tab */}
          <button
            onClick={() => handleTabClick('attachments')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
              activeTab === 'attachments'
                ? 'bg-foreground/10 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            )}
          >
            <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Attachments</span>
          </button>

          {/* Cards toggle */}
          <button
            onClick={toggleCardsEnabled}
            title={cardsEnabled ? 'Card retrieval enabled' : 'Card retrieval disabled'}
            className={cn(
              'p-1.5 rounded transition-colors',
              cardsEnabled
                ? 'text-foreground hover:bg-muted/30'
                : 'text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/30'
            )}
          >
            <LibraryBig className={cn('w-4 h-4', !cardsEnabled && 'opacity-50')} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Content - always mount all tabs to preserve state, hide with CSS */}
      <div className="flex-1 overflow-hidden relative">
        {/* Document tab - clean canvas (no annotations) */}
        <div className={cn('absolute inset-0', activeTab === 'document' ? '' : 'invisible pointer-events-none')}>
          <div className="h-full overflow-y-auto">
            {foolscapMarkdown ? (
              <FFoolscapContent
                foolscapMarkdown={foolscapMarkdown}
                streaming={isStreaming}
                onCardClick={handleCardClick}
              />
            ) : (
              <div className="h-full flex items-center justify-center px-6 text-center">
                <div className="text-muted-foreground text-sm max-w-sm space-y-2">
                  <p className="font-medium text-foreground">Your Foolscap Document</p>
                  <p>
                    As we discuss your story, I'll build out your one-page structure here.
                  </p>
                  <p className="text-xs">
                    Start chatting to begin building your foolscap.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edits tab - shows annotated diff (Zinsser pattern) */}
        <div className={cn('absolute inset-0', activeTab === 'edits' ? '' : 'invisible pointer-events-none')}>
          <FEditsPanel onCardClick={handleCardClick} />
        </div>

        {/* Cards tab */}
        <div className={cn('absolute inset-0', activeTab === 'cards' ? '' : 'invisible pointer-events-none')}>
          <FCardsPanel />
        </div>

        {/* Attachments tab */}
        <div className={cn('absolute inset-0', activeTab === 'attachments' ? '' : 'invisible pointer-events-none')}>
          <FAttachmentsPanel />
        </div>
      </div>
    </div>
  )
}
